import type {
  AiReviewDataSource,
  AiReviewChatRequest,
  AiReviewProposal,
  AiReviewSessionSummary,
  AiReviewWorkspace,
} from '../models/AiReviewDataSource';
import {
  CAPTURE_REPLAY_ORGANIZATION_IDS,
  isNorthstarCaptureReplayOrganization,
  NORTHSTAR_CAPTURE_REPLAY_ORGANIZATION,
} from '../Environment';

export const AI_REVIEW_REPLAY_VERSION = 'northstar-ai-review-decisions-v1';
export const AI_REVIEW_REPLAY_TALENT_ID = 'northstar-jordan-avery-replay';
export const AI_REVIEW_REPLAY_PROMPT = 'During this review period, Jordan completed eight scheduled equipment inspections and submitted the service records on time. One ambiguous safety condition was documented but not escalated until I followed up. I want the review to recognize the consistent inspection and documentation work while making earlier safety escalation a clear development priority.';

export interface AiReviewReplayHostDraft {
  EscalationRating: string;
  EscalationNote: string;
  DevelopmentGoal: string;
}

export interface AiReviewReplayFixture {
  Version: string;
  IsSynthetic: true;
  ReadOnly: true;
  OrganizationName: string;
  TalentName: string;
  PositionName: string;
  Prompt: string;
  BaselineHostDraft: AiReviewReplayHostDraft;
  Proposals: readonly AiReviewProposal[];
}

const deepFreeze = <T,>(value: T): T => {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value as Record<string, unknown>).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
};

export const AI_REVIEW_REPLAY_CANONICAL: AiReviewReplayFixture = deepFreeze({
  Version: AI_REVIEW_REPLAY_VERSION,
  IsSynthetic: true,
  ReadOnly: true,
  OrganizationName: NORTHSTAR_CAPTURE_REPLAY_ORGANIZATION,
  TalentName: 'Jordan Avery',
  PositionName: 'Field Service Technician',
  Prompt: AI_REVIEW_REPLAY_PROMPT,
  BaselineHostDraft: {
    EscalationRating: 'Sometimes',
    EscalationNote: 'Ambiguous safety condition required follow-up — August 6, 2026.',
    DevelopmentGoal: '',
  },
  Proposals: [
    {
      Id: 'ai-proficiency-escalation',
      Type: 'Proficiency',
      TargetProficiencyId: 'escalates-risks',
      TargetName: 'Escalates ambiguous safety risks before continuing work',
      CurrentValue: 'Sometimes',
      ProposedValue: 'Usually',
      CurrentNotes: 'Ambiguous safety condition required follow-up — August 6, 2026.',
      ProposedNotes: 'Completed eight scheduled inspections with timely documentation. One ambiguous safety condition required supervisor follow-up before it was escalated.',
      Rationale: 'The evidence supports consistent inspection and documentation work, while the follow-up shows that earlier escalation remains a development priority.',
      Evidence: 'Completed eight scheduled inspections — July 12, 2026; Ambiguous safety condition required follow-up — August 6, 2026.',
      Status: 'Pending',
      CanApply: true,
      RowVersion: 'replay-1',
    },
    {
      Id: 'ai-goal-too-broad',
      Type: 'DevelopmentGoal',
      TargetName: 'Inspects commercial equipment systematically',
      GoalTitle: 'Redesign the full inspection program',
      GoalNarrative: 'Replace the organization’s inspection program this quarter.',
      Rationale: 'This broad organizational redesign is not supported by the specific review evidence.',
      Evidence: 'The review evidence concerns Jordan’s completed inspections and one delayed escalation.',
      Status: 'Pending',
      CanApply: true,
      RowVersion: 'replay-1',
    },
    {
      Id: 'ai-goal-revised',
      Type: 'DevelopmentGoal',
      TargetName: 'Escalates ambiguous safety risks before continuing work',
      GoalTitle: 'Use the safety-escalation checklist consistently',
      GoalNarrative: 'For the next 30 days, use the safety-escalation checklist on every ambiguous condition and review the completed entries with Northstar Trainer each week.',
      Rationale: 'A focused 30-day action directly addresses the evidence and gives the supervisor a clear coaching cadence.',
      Evidence: 'Ambiguous safety condition required follow-up — August 6, 2026.',
      Status: 'Pending',
      CanApply: true,
      RowVersion: 'replay-1',
    },
    {
      Id: 'ai-template-advisory',
      Type: 'TemplateChange',
      TargetName: 'Safety escalation standard',
      CurrentValue: 'Existing shared template criterion',
      ProposedValue: 'Review wording with template administrators',
      Rationale: 'This is advisory only and must not change the current assessment or shared template.',
      Evidence: 'One review observation is not enough to revise a shared organizational standard automatically.',
      Status: 'Pending',
      CanApply: false,
      RowVersion: 'replay-1',
    },
  ],
});

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
const abortIfNeeded = (signal?: AbortSignal) => {
  if (signal?.aborted)
    throw new DOMException('The AI review replay request was aborted.', 'AbortError');
};

const initialWorkspace = (): AiReviewWorkspace => ({
  SessionId: 'northstar-ai-review-session',
  TalentId: AI_REVIEW_REPLAY_TALENT_ID,
  TemplateRevisionId: 'northstar-field-service-technician-v1',
  IsAiConfigured: true,
  IsFinalized: false,
  ConfigurationMessage: null,
  ContextSummary: 'Field Service Technician Success Profile, prior published review, two review-period events, and one active 30-day development goal.',
  Messages: [{
    Id: 'assistant-intro',
    Role: 'assistant',
    Content: 'Share a review-period observation. I will organize the evidence into suggestions, but nothing changes until you decide.',
    Source: 'Text',
  }],
  Proposals: [],
});

const historicalWorkspace = (): AiReviewWorkspace => ({
  ...initialWorkspace(),
  SessionId: 'northstar-finalized-history',
  IsFinalized: true,
  Messages: [
    { Id: 'history-user', Role: 'user', Content: 'Jordan completed scheduled inspections and documented findings promptly.', Source: 'Text' },
    { Id: 'history-ai', Role: 'assistant', Content: 'The prior review retained the evidence and the supervisor finalized the rating.', Source: 'Text' },
  ],
  Proposals: [],
});

export const aiReviewReplayFingerprint = (): string => JSON.stringify(AI_REVIEW_REPLAY_CANONICAL);

/**
 * In-memory adapter for the released AiReviewPanel. It deliberately has no
 * external transport or product-client dependency. Every action mutates only
 * these cloned JavaScript objects.
 */
export class AiReviewReplayAdapter implements AiReviewDataSource {
  private workspace: AiReviewWorkspace = initialWorkspace();
  private hostDraft: AiReviewReplayHostDraft = clone(AI_REVIEW_REPLAY_CANONICAL.BaselineHostDraft);

  reset(): void {
    this.workspace = initialWorkspace();
    this.hostDraft = clone(AI_REVIEW_REPLAY_CANONICAL.BaselineHostDraft);
  }

  getHostDraft(): AiReviewReplayHostDraft { return clone(this.hostDraft); }
  getWorkspace(): AiReviewWorkspace { return clone(this.workspace); }

  async GetConversation(talentId: string, signal?: AbortSignal): Promise<AiReviewWorkspace> {
    abortIfNeeded(signal);
    if (talentId !== AI_REVIEW_REPLAY_TALENT_ID)
      throw new Error('Unknown synthetic talent fixture.');
    return clone(this.workspace);
  }

  async GetSessions(talentId: string, signal?: AbortSignal): Promise<AiReviewSessionSummary[]> {
    abortIfNeeded(signal);
    if (talentId !== AI_REVIEW_REPLAY_TALENT_ID)
      throw new Error('Unknown synthetic talent fixture.');
    return [{
      Id: 'northstar-finalized-history', TalentId: talentId, Status: 'Finalized',
      CreatedDate: '2026-04-15T12:00:00.000Z', FinalizedDate: '2026-04-15T12:00:00.000Z',
      TemplateName: 'Field Service Technician Success Profile',
    }];
  }

  async GetSession(sessionId: string, signal?: AbortSignal): Promise<AiReviewWorkspace> {
    abortIfNeeded(signal);
    if (sessionId !== 'northstar-finalized-history')
      throw new Error('Unknown synthetic history fixture.');
    return historicalWorkspace();
  }

  async Start(talentId: string, signal?: AbortSignal): Promise<AiReviewWorkspace> {
    return this.GetConversation(talentId, signal);
  }

  async Chat(talentId: string, request: AiReviewChatRequest, signal?: AbortSignal): Promise<AiReviewWorkspace> {
    abortIfNeeded(signal);
    if (talentId !== AI_REVIEW_REPLAY_TALENT_ID || request.SessionId !== this.workspace.SessionId)
      throw new Error('Unknown synthetic AI review session.');
    if (request.Message.trim() !== AI_REVIEW_REPLAY_PROMPT)
      throw new Error('Capture replay accepts only the approved synthetic observation.');

    this.workspace = {
      ...this.workspace,
      Messages: [
        ...this.workspace.Messages,
        { Id: 'approved-observation', Role: 'user', Content: AI_REVIEW_REPLAY_PROMPT, Source: 'Text' },
        { Id: 'approved-response', Role: 'assistant', Content: 'I organized the observation into evidence-backed suggestions for your review. Nothing has been applied.', Source: 'Text' },
      ],
      Proposals: clone([...AI_REVIEW_REPLAY_CANONICAL.Proposals]),
    };
    return clone(this.workspace);
  }

  private async decide(proposalId: string, status: 'Accepted' | 'Rejected' | 'Undone', signal?: AbortSignal): Promise<AiReviewWorkspace> {
    abortIfNeeded(signal);
    const proposal = this.workspace.Proposals.find(candidate => candidate.Id === proposalId);
    if (!proposal)
      throw new Error('Unknown synthetic proposal.');
    if (status === 'Accepted' && !proposal.CanApply)
      throw new Error('Advisory template proposals cannot be applied from a review.');

    this.workspace = {
      ...this.workspace,
      Proposals: this.workspace.Proposals.map(candidate => candidate.Id === proposalId ? { ...candidate, Status: status } : candidate),
    };

    if (proposal.Id === 'ai-proficiency-escalation') {
      this.hostDraft = status === 'Accepted'
        ? { ...this.hostDraft, EscalationRating: `${proposal.ProposedValue}`, EscalationNote: proposal.ProposedNotes ?? '' }
        : status === 'Undone'
          ? clone(AI_REVIEW_REPLAY_CANONICAL.BaselineHostDraft)
          : this.hostDraft;
    }
    if (proposal.Id === 'ai-goal-revised') {
      this.hostDraft = status === 'Accepted'
        ? { ...this.hostDraft, DevelopmentGoal: proposal.GoalNarrative ?? '' }
        : status === 'Undone'
          ? { ...this.hostDraft, DevelopmentGoal: '' }
          : this.hostDraft;
    }
    return clone(this.workspace);
  }

  AcceptProposal(proposalId: string, _rowVersion?: string | null, signal?: AbortSignal): Promise<AiReviewWorkspace> {
    return this.decide(proposalId, 'Accepted', signal);
  }
  RejectProposal(proposalId: string, _rowVersion?: string | null, signal?: AbortSignal): Promise<AiReviewWorkspace> {
    return this.decide(proposalId, 'Rejected', signal);
  }
  UndoProposal(proposalId: string, _rowVersion?: string | null, signal?: AbortSignal): Promise<AiReviewWorkspace> {
    return this.decide(proposalId, 'Undone', signal);
  }
}

export default class AiReviewReplayService {
  private readonly allowedOrganizationIds: readonly string[];
  constructor(allowedOrganizationIds: readonly string[] = CAPTURE_REPLAY_ORGANIZATION_IDS) {
    this.allowedOrganizationIds = Object.freeze([...allowedOrganizationIds]);
  }
  Get(selectedOrganizationId: string, selectedOrganizationName: string): AiReviewReplayFixture {
    if (!isNorthstarCaptureReplayOrganization(selectedOrganizationId, selectedOrganizationName, this.allowedOrganizationIds))
      throw new Error('AI review replay is available only for the synthetic Northstar organization.');
    return clone(AI_REVIEW_REPLAY_CANONICAL);
  }
}
